import React, { Fragment, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { roleApi } from '@app/store';
import { RoleDTO } from '@app/common';
import { Button, CardButtonActionMode, Form, Panel } from '@app/components';
import { RoleForm } from './RoleForm';
import {
  RoleFormValues,
  transformToUpdatableFormRole,
  transformRoleFormValuesToPayload,
} from './role-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { RoleCard } from './RoleCard';
import { CheckPermissions } from '@app/components/CheckPermissions';
import { explodePermission, IRole } from '@tickeat/permissions';
import { AppPath } from '@tickeat/types';

interface RoleUpdateProps {
  role?: Partial<IRole>;
  isFetching: boolean;
  toggle: (mode: CardButtonActionMode) => void;
  onDelete: (role: Partial<IRole>) => void;
}

export const RoleUpdate = ({ role, isFetching, toggle, onDelete }: RoleUpdateProps) => {
  const toast = useToast();

  const triptych = useTriptych();

  const [update, { isLoading, error: apiErrors }] = roleApi.useUpdateOneRolesMutation();

  const formMethods = useForm<RoleFormValues>({
    resolver: classValidatorResolver(RoleDTO),
    defaultValues: transformToUpdatableFormRole(role),
  });

  const onUpdate = async (formValues: RoleFormValues) => {
    try {
      const updatedRole = await update({
        _id: role?._id!,
        ...transformRoleFormValuesToPayload(formValues),
      }).unwrap();

      triptych.close({ mode: 'UPDATE', data: updatedRole });

      toast.success('Votre rôle a bien été modifié.');
    } catch {
      toast.error('Une erreur est survenue lors de la modification de votre rôle.');
    }
  };

  useEffect(() => {
    formMethods.reset(transformToUpdatableFormRole(role));
  }, [role, formMethods]);

  const defaultOpenGroups = useMemo(() => {
    return (
      role?.permissions?.reduce((acc, perm) => {
        const { path } = explodePermission(perm);
        acc[path] = true;
        return acc;
      }, {} as Partial<Record<AppPath, boolean>>) ?? {}
    );
  }, [role]);

  return (
    <Fragment>
      <RoleCard role={role} formMethods={formMethods} toggle={toggle} />

      <Panel.Content>
        <Form formMethods={formMethods} apiErrors={apiErrors} isLoading={isFetching}>
          <Panel.FormContainer>
            <CheckPermissions permissions={['dashboard::roles.delete']}>
              <Panel.Header>
                <Button title="Supprimer" onPress={() => onDelete(role!)} bordered important />
              </Panel.Header>
            </CheckPermissions>

            <RoleForm defaultOpenGroups={defaultOpenGroups} />
          </Panel.FormContainer>

          <Panel.Actions>
            <Button title="Retour" grow large bordered onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              disabled={isFetching}
              onPress={formMethods.handleSubmit(onUpdate)}
              title="Valider"
              grow
              large
            />
          </Panel.Actions>
        </Form>
      </Panel.Content>
    </Fragment>
  );
};
